import React, { useState } from 'react';
import {
  XCircle, CheckCircle, Loader2, Send, Users, Sparkles, Zap,
  UserRound, Mail, Phone, Building2
} from 'lucide-react';
import { API_URL } from '../../config';

// ============================================================
// Formulaire de demande générique — service géré par un humain
// (pas de self-service). Utilisé par Page Facebook et WhatsApp.
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export function RequestServiceCard({ service, title, description, accessStatus, onSent, rejected }) {
  const [form, setForm] = useState({
    full_name: accessStatus?.user_name || localStorage.getItem('user_name') || '',
    email: accessStatus?.user_email || '',
    phone: '',
    business_name: '',
    message: '',
  });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState(null);

  const set = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name.trim() || !form.email.trim()) {
      setError('Nom et email sont requis');
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}/api/bot/service-requests`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ service, ...form })
      });
      const data = await res.json();
      if (data.success) {
        setSent(true);
        onSent && onSent();
      } else {
        setError(data.message || 'Erreur lors de l\'envoi');
      }
    } catch (e) {
      setError('Erreur serveur');
    } finally {
      setSending(false);
    }
  };

  if (sent) return <PendingRequestCard />;

  return (
    <div className="relative mb-8">
      {/* Glow décoratif */}
      <div className="absolute -top-20 left-1/2 -translate-x-1/2 w-72 h-72 bg-indigo-600/20 rounded-full blur-3xl pointer-events-none" />

      <div className="relative bg-gradient-to-b from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 overflow-hidden">
        {/* Bordure lumineuse en haut */}
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-400 to-transparent" />

        <div className="text-center mb-8">
          <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-950 to-purple-950 border border-white/10 flex items-center justify-center mx-auto mb-5 shadow-2xl shadow-indigo-500/20 p-2">
            <img src="/tdi-digital-logo.png" alt="TDI DIGITAL" className="w-full h-full object-contain rounded-2xl" />
          </div>
          <h2 className="text-xl md:text-2xl font-bold text-white mb-2">
            {rejected ? 'Renouveler votre demande' : (title || 'Un vrai humain configure votre agent')}
          </h2>
          <p className="text-blue-200 text-sm max-w-lg mx-auto leading-relaxed">
            {description || (
              <>Chez <span className="text-white font-semibold">TDI DIGITAL</span>, votre agent IA
              n'est pas livré tel quel : notre équipe le configure et le suit personnellement pour garantir
              une vraie qualité de service.</>
            )}
          </p>

          {/* Badges de confiance */}
          <div className="flex items-center justify-center gap-2 flex-wrap mt-5">
            <TrustBadge icon={Zap} text="Réponse rapide" />
            <TrustBadge icon={Users} text="Suivi humain dédié" />
            <TrustBadge icon={Sparkles} text="Configuration sur-mesure" />
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center gap-2 max-w-lg mx-auto">
            <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
          </div>
        )}

        <form onSubmit={submit} className="max-w-lg mx-auto space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField icon={UserRound}>
              <input
                value={form.full_name}
                onChange={(e) => set('full_name', e.target.value)}
                placeholder="Votre nom complet"
                required
                className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none"
              />
            </FormField>
            <FormField icon={Mail}>
              <input
                type="email"
                value={form.email}
                onChange={(e) => set('email', e.target.value)}
                placeholder="Votre email"
                required
                className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none"
              />
            </FormField>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <FormField icon={Phone}>
              <input
                value={form.phone}
                onChange={(e) => set('phone', e.target.value)}
                placeholder="Téléphone (WhatsApp de préférence)"
                className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none"
              />
            </FormField>
            <FormField icon={Building2}>
              <input
                value={form.business_name}
                onChange={(e) => set('business_name', e.target.value)}
                placeholder="Nom de votre page / entreprise"
                className="w-full bg-transparent text-white text-sm placeholder-gray-500 outline-none"
              />
            </FormField>
          </div>

          <div>
            <label className="text-blue-200 text-xs font-medium mb-1.5 block">
              Parlez-nous de votre besoin
            </label>
            <textarea
              value={form.message}
              onChange={(e) => set('message', e.target.value)}
              placeholder="Ex : « Nous vendons des vêtements, on reçoit beaucoup de messages qu'on n'arrive pas à suivre... »"
              rows={3}
              className="w-full bg-white/[0.06] border border-white/10 focus:border-indigo-400/50 rounded-xl px-4 py-3 text-white text-sm placeholder-gray-500 outline-none transition-colors resize-none"
            />
            <p className="text-gray-500 text-xs mt-1.5">
              💡 Même en 2 lignes : plus on comprend votre activité, plus vite on peut vous proposer la bonne configuration.
            </p>
          </div>

          <button
            type="submit"
            disabled={sending}
            className="w-full flex items-center justify-center gap-2 px-6 py-4 rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-500 hover:to-purple-500 text-white font-bold shadow-xl shadow-indigo-900/40 transition-all hover:scale-[1.02] disabled:opacity-60 disabled:hover:scale-100"
          >
            {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            {sending ? 'Envoi en cours...' : 'Envoyer ma demande'}
          </button>
          <p className="text-center text-gray-500 text-xs">
            Notre équipe vous recontacte personnellement — aucun engagement.
          </p>
        </form>
      </div>
    </div>
  );
}

function TrustBadge({ icon: Icon, text }) {
  return (
    <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/5 border border-white/10 text-blue-200 text-xs font-medium">
      <Icon className="w-3.5 h-3.5 text-indigo-300" /> {text}
    </span>
  );
}

function FormField({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2.5 bg-white/[0.06] border border-white/10 focus-within:border-indigo-400/50 rounded-xl px-4 py-3 transition-colors">
      <Icon className="w-4 h-4 text-gray-500 flex-shrink-0" />
      <div className="flex-1">{children}</div>
    </div>
  );
}

export function PendingRequestCard({ text }) {
  return (
    <div className="relative mb-8">
      <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-64 bg-green-500/10 rounded-full blur-3xl pointer-events-none" />
      <div className="relative bg-gradient-to-b from-white/[0.07] to-white/[0.02] backdrop-blur-xl border border-white/10 rounded-3xl p-6 md:p-10 text-center overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-green-400 to-transparent" />

        <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-indigo-950 to-purple-950 border border-white/10 flex items-center justify-center mx-auto mb-5 shadow-2xl shadow-green-500/10 p-2">
          <img src="/tdi-digital-logo.png" alt="TDI DIGITAL" className="w-full h-full object-contain rounded-2xl" />
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/15 border border-green-500/30 text-green-300 text-xs font-semibold mb-4">
          <CheckCircle className="w-3.5 h-3.5" /> Demande envoyée
        </div>
        <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Merci !</h2>
        <p className="text-blue-200 text-sm max-w-md mx-auto leading-relaxed">
          {text || (
            <>Notre équipe <span className="text-white font-semibold">TDI DIGITAL</span> a bien reçu votre demande
            et vous contacte personnellement très rapidement pour activer votre agent IA.</>
          )}
        </p>
      </div>
    </div>
  );
}
